"use client";

import { forwardRef, useState, useRef, useCallback, useEffect } from "react";
import type { HTMLAttributes, ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../../lib/utils";

export interface GalleryImage {
  id: string;
  url: string;
  alt: string;
  thumbnail?: string;
}

export interface GalleryProps extends HTMLAttributes<HTMLDivElement> {
  images: GalleryImage[];
  aspectRatio?: "16/9" | "4/3" | "3/2" | "1/1";
  rounded?: "none" | "sm" | "md" | "lg" | "xl" | "2xl";
  showNavigation?: boolean;
  showIndicators?: boolean;
  onImageClick?: (image: GalleryImage, index: number) => void;
  onImageChange?: (index: number) => void;
  emptyState?: ReactNode;
}

const Gallery = forwardRef<HTMLDivElement, GalleryProps>(
  (
    {
      images,
      aspectRatio = "16/9",
      rounded = "lg",
      showNavigation = true,
      showIndicators = true,
      onImageClick,
      onImageChange,
      emptyState,
      className,
      ...props
    },
    ref,
  ) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const aspectRatioClass = {
      "16/9": "gallery-aspect-16-9",
      "4/3": "gallery-aspect-4-3",
      "3/2": "gallery-aspect-3-2",
      "1/1": "gallery-aspect-1-1",
    }[aspectRatio];

    const roundedClass = {
      none: "",
      sm: "gallery-rounded-sm",
      md: "gallery-rounded-md",
      lg: "gallery-rounded-lg",
      xl: "gallery-rounded-xl",
      "2xl": "gallery-rounded-2xl",
    }[rounded];

    const scrollToIndex = useCallback(
      (index: number) => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const itemWidth = container.scrollWidth / images.length;
        container.scrollTo({
          left: itemWidth * index,
          behavior: "smooth",
        });
      },
      [images.length],
    );

    const handlePrevious = useCallback(() => {
      const newIndex =
        currentIndex === 0 ? images.length - 1 : currentIndex - 1;
      setCurrentIndex(newIndex);
      scrollToIndex(newIndex);
      onImageChange?.(newIndex);
    }, [currentIndex, images.length, scrollToIndex, onImageChange]);

    const handleNext = useCallback(() => {
      const newIndex =
        currentIndex === images.length - 1 ? 0 : currentIndex + 1;
      setCurrentIndex(newIndex);
      scrollToIndex(newIndex);
      onImageChange?.(newIndex);
    }, [currentIndex, images.length, scrollToIndex, onImageChange]);

    const handleIndicatorClick = useCallback(
      (index: number) => {
        setCurrentIndex(index);
        scrollToIndex(index);
        onImageChange?.(index);
      },
      [scrollToIndex, onImageChange],
    );

    const handleScroll = useCallback(() => {
      const container = scrollContainerRef.current;
      if (!container || images.length === 0) return;

      const itemWidth = container.scrollWidth / images.length;
      const newIndex = Math.round(container.scrollLeft / itemWidth);

      if (
        newIndex !== currentIndex &&
        newIndex >= 0 &&
        newIndex < images.length
      ) {
        setCurrentIndex(newIndex);
        onImageChange?.(newIndex);
      }
    }, [images.length, currentIndex, onImageChange]);

    useEffect(() => {
      const container = scrollContainerRef.current;
      if (!container) return;

      container.addEventListener("scrollend", handleScroll);
      return () => container.removeEventListener("scrollend", handleScroll);
    }, [handleScroll]);

    // Empty state
    if (!images || images.length === 0) {
      return (
        <div
          ref={ref}
          className={cn(
            "vehicle-gallery",
            aspectRatioClass,
            roundedClass,
            "gallery-empty",
            className,
          )}
          {...props}
        >
          {emptyState || (
            <div className="gallery-empty-content">
              <span className="gallery-empty-text">No images available</span>
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={cn("vehicle-gallery", roundedClass, className)}
        {...props}
      >
        {/* Scroll container */}
        <div
          ref={scrollContainerRef}
          className={cn("gallery-scroll", aspectRatioClass)}
        >
          {images.map((image, index) => (
            <div
              key={image.id}
              className="gallery-item"
              onClick={() => onImageClick?.(image, index)}
              role={onImageClick ? "button" : undefined}
              tabIndex={onImageClick ? 0 : undefined}
              onKeyDown={(e) => {
                if (onImageClick && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  onImageClick(image, index);
                }
              }}
            >
              <img
                src={image.url}
                alt={image.alt}
                className="gallery-image"
                loading={index === 0 ? "eager" : "lazy"}
                draggable={false}
              />
            </div>
          ))}
        </div>

        {/* Navigation arrows */}
        {showNavigation && images.length > 1 && (
          <>
            <button
              type="button"
              className="gallery-nav gallery-nav-prev"
              onClick={handlePrevious}
              aria-label="Previous image"
            >
              <ChevronLeft className="gallery-nav-icon" />
            </button>
            <button
              type="button"
              className="gallery-nav gallery-nav-next"
              onClick={handleNext}
              aria-label="Next image"
            >
              <ChevronRight className="gallery-nav-icon" />
            </button>
          </>
        )}

        {/* Indicators */}
        {showIndicators && images.length > 1 && (
          <div className="gallery-indicators">
            {images.map((_, index) => (
              <button
                key={index}
                type="button"
                className={cn(
                  "gallery-indicator",
                  index === currentIndex && "gallery-indicator-active",
                )}
                onClick={() => handleIndicatorClick(index)}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    );
  },
);

Gallery.displayName = "Gallery";

export { Gallery };
